import { useMemo } from 'react'
import {
  TRANSACTION_DTYPE,
  CHARGE_TRANSACTION_DTYPE,
  ADJUSTMENT_TRANSACTION_DTYPE,
  LEDGER_ENTRY_TYPE,
} from 'Data/payments.js'

let DTYPE_TO_LEDGER_ENTRY_TYPE = /** @type {const} */ ({
  [TRANSACTION_DTYPE.ChargeTransfer]: LEDGER_ENTRY_TYPE.CHARGE_TRANSFER,
  [TRANSACTION_DTYPE.TransferCharge]: LEDGER_ENTRY_TYPE.TRANSFER_CHARGE,
  [TRANSACTION_DTYPE.InstallmentCharge]: LEDGER_ENTRY_TYPE.INSTALLMENT_CHARGE,
  [TRANSACTION_DTYPE.PaymentPlan]: LEDGER_ENTRY_TYPE.PAYMENT_PLAN,
  [TRANSACTION_DTYPE.PaymentPlanRollback]:
    LEDGER_ENTRY_TYPE.PAYMENT_PLAN_ROLLBACK,
  [TRANSACTION_DTYPE.Payment]: LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
  [TRANSACTION_DTYPE.UnappliedPayment]: LEDGER_ENTRY_TYPE.UNAPPLIED_PAYMENT,
})

let LEDGER_ENTRY_TYPE_ORDER = [
  LEDGER_ENTRY_TYPE.CHARGE,
  LEDGER_ENTRY_TYPE.INSTALLMENT_CHARGE,
  LEDGER_ENTRY_TYPE.ADJUSTMENT,
  LEDGER_ENTRY_TYPE.TRANSFER_CHARGE,
  LEDGER_ENTRY_TYPE.CHARGE_TRANSFER,
  LEDGER_ENTRY_TYPE.PAYMENT_PLAN,
  LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
]

export default function useDataTransform(props, data) {
  return useMemo(() => {
    if (!data) return data

    let result = []
    for (let transaction of data) {
      if (CHARGE_TRANSACTION_DTYPE.includes(transaction.dtype)) {
        if (
          result.length > 0 &&
          canAggregateTo(
            result[result.length - 1],
            LEDGER_ENTRY_TYPE.CHARGE,
            transaction
          )
        ) {
          // an aggregation of charges it's already started, appending to it
          result[result.length - 1].transactions.push(transaction)
        } else {
          // start a new aggregation entry for the charges
          result.push({
            type: LEDGER_ENTRY_TYPE.CHARGE,
            id: transaction.id,
            created_at: transaction.sys_created,
            sort_date: transaction.effective_date ?? transaction.sys_created,
            created_by: transaction.sys_created_by,
            receivable_id: transaction.receivable_id,
            created_by_user: transaction.created_by_user,
            payment_account: transaction.receivable.payment_account,
            transactions: [transaction],
          })
        }
      } else if (ADJUSTMENT_TRANSACTION_DTYPE.includes(transaction.dtype)) {
        if (
          result.length > 0 &&
          canAggregateTo(
            result[result.length - 1],
            LEDGER_ENTRY_TYPE.ADJUSTMENT,
            transaction
          )
        ) {
          // an aggregation of adjustments it's already started, appending to it
          result[result.length - 1].transactions.push(transaction)
        } else {
          // start a new aggregation entry for the adjustments
          result.push({
            type: LEDGER_ENTRY_TYPE.ADJUSTMENT,
            id: transaction.id,
            created_at: transaction.sys_created,
            sort_date: transaction.effective_date ?? transaction.sys_created,
            created_by: transaction.sys_created_by,
            receivable_id: transaction.receivable_id,
            created_by_user: transaction.created_by_user,
            payment_account: transaction.receivable.payment_account,
            transactions: [transaction],
          })
        }
      } else if (transaction.dtype === TRANSACTION_DTYPE.Payment) {
        result.push({
          id: transaction.id,
          // only applied payments should be returned at this stage
          type: LEDGER_ENTRY_TYPE.APPLIED_PAYMENT,
          created_at: transaction.sys_created,
          sort_date: transaction.effective_date ?? transaction.sys_created,
          created_by: transaction.sys_created_by,
          created_by_user: transaction.created_by_user,
          payment_account:
            transaction.receivable?.payment_account ??
            transaction.applied_payment?.payment?.account ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.payment_account,
          receivable_id: transaction.receivable_id ?? null,
          transaction,
        })
      } else {
        // installment's created date is when payment plan was accepted, so not very useful
        let created_at =
          transaction.dtype === TRANSACTION_DTYPE.InstallmentCharge
            ? transaction.receivable.due_date
            : (transaction.effective_date ?? transaction.sys_created)
        result.push({
          id: transaction.id,
          type: DTYPE_TO_LEDGER_ENTRY_TYPE[transaction.dtype],
          created_at: created_at,
          sort_date: created_at,
          created_by: transaction.sys_created_by,
          created_by_user: transaction.created_by_user,
          payment_account:
            transaction.receivable?.payment_account ??
            transaction.applied_payment?.payment?.account ??
            transaction.unapplied_payment?.applied_payment?.payment
              ?.payment_account,
          receivable_id: transaction.receivable_id ?? null,
          transaction,
        })
      }
    }

    // Sort by sys_created first, then by transaction type order
    result.sort((a, b) => {
      // Sort by sys_created (descending - newest first)
      let dateComparison = new Date(a.sort_date) - new Date(b.sort_date)
      if (dateComparison !== 0) return dateComparison

      let getOrder = type => {
        let i = LEDGER_ENTRY_TYPE_ORDER.indexOf(type)
        return i === -1 ? 99 : i
      }
      return getOrder(a.type) - getOrder(b.type)
    })

    return result
  }, [data])
}

function canAggregateTo(last, type, transaction) {
  return (
    last.type === type &&
    last.created_by === transaction.sys_created_by &&
    last.receivable_id === transaction.receivable_id
  )
}
